import { Text, Heading, Flex, Grid, Container, Box } from '@radix-ui/themes';
import { IconMarker } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/icons/icons';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/best-deal/contexts/use-tenant-context';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/best-deal/lib/utils/get-variant-value';
import { Button } from '@trashlab/sitesapp/src/organizations/best-deal/template/components/shared/button';
import { Image } from '@trashlab/sitesapp/src/organizations/best-deal/components/shared/image';
import ZoneMapWithGeoAreas from '@trashlab/viteapp/src/components/settings/zone-map-with-geoareas';
import { Suspense } from 'react';
import { asAddress } from '@trashlab/api/src/library/server/utils/json-compatibility';

// TODO: Remove this once Red E Bins have set their service areas
const RED_E_BINS_TENANT_ID = '7503cb07-ed5c-49f3-bc55-290b297a971d';

const HARDCODED_SERVICE_AREAS = [
  'Anoka County',
  'Ramsey',
  'St. Paul',
  'Carver',
  'Scott',
  'Dakota',
  'Washington',
  'Hennepin',
  'Minneapolis',
  'St. Louis Park',
];

const MapPlaceholder = () => {
  return (
    <div className="w-full h-[600px] bg-white rounded-xl shadow-sm flex items-center justify-center">
      <div className="text-gray-400">Loading service area map...</div>
    </div>
  );
};

export const ServiceAreasList = () => {
  const { website } = useTenantContext();
  const orgAddress = asAddress(website?.organization?.address as any);

  const serviceAreaMap = useVariantValue('SERVICE_AREA_MAP_IMAGE', '');

  const serviceAreaTitle = useVariantValue(
    'SERVICE_AREA_TITLE',
    `${
      orgAddress?.state ?? 'CA'
    }'s most expansive service coverage`,
  );
  const serviceAreaSubtitle = useVariantValue(
    'SERVICE_AREA_SUBTITLE',
    "Check to see if you're within our service area",
  );

  if (!website?.organization?.id) {
    return null;
  }

  const isSpecialTenant = website.organization.id === RED_E_BINS_TENANT_ID;

  if (isSpecialTenant) {
    return (
      <Container size="4" p="8">
        <Grid columns={{ initial: '1', sm: '2' }} gap="6">
          <Flex
            direction="column"
            gap="4"
            className="my-auto w-full max-md:mt-10"
          >
            <Flex direction="column" gap="4" className="mb-8">
              <Text
                size="2"
                weight="bold"
                className="text-neutral-400"
                data-variable="SERVICE_AREA_TITLE"
              >
                {serviceAreaTitle}
              </Text>
              <Heading
                size="8"
                weight="bold"
                data-variable="SERVICE_AREA_SUBTITLE"
              >
                Our service areas
              </Heading>
            </Flex>
            <Text size="3" className="mb-2">
              {serviceAreaSubtitle}
            </Text>

            <Grid
              columns="2"
              gap="4"
              className="max-md:grid-cols-1 justify-items-start"
            >
              {HARDCODED_SERVICE_AREAS.map((area, index) => (
                <Button
                  key={`${area}-${index}`}
                  variant="soft"
                  size="small"
                  className="pointer-events-none cursor-default"
                  leftIcon={<IconMarker className="h-5 w-5 text-primary" />}
                >
                  <Flex direction="column" align="start">
                    <Text className="font-semibold">{area}</Text>
                  </Flex>
                </Button>
              ))}
            </Grid>
          </Flex>

          <Box className="h-full flex flex-col justify-center">
            {serviceAreaMap ? (
              <Box className="relative shadow-lg rounded-2xl overflow-hidden">
                <Image
                  src={serviceAreaMap}
                  alt="Service Area Map"
                  className="w-full h-full object-cover rounded-2xl"
                />
              </Box>
            ) : (
              <Box className="bg-gray-100 h-[450px] rounded-2xl flex items-center justify-center">
                <Text className="text-gray-500">
                  Service area map image not set
                </Text>
              </Box>
            )}
          </Box>
        </Grid>
      </Container>
    );
  }

  return (
    <Container size="4" className="p-8 md:py-12">
      <Flex direction="column" gap="6" className="w-full">
        <Flex direction="column" gap="2">
          <Text
            size="2"
            weight="bold"
            className="text-neutral-400"
            data-variable="SERVICE_AREA_TITLE"
          >
            {serviceAreaTitle}
          </Text>
          <Heading
            size="8"
            weight="bold"
            data-variable="SERVICE_AREA_SECTION_TITLE"
          >
            Our service areas
          </Heading>
          <Text size="3" className="text-neutral-600">
            {serviceAreaSubtitle}
          </Text>
        </Flex>
        <Suspense fallback={<MapPlaceholder />}>
          <div className="w-full h-[600px] bg-white rounded-xl shadow-sm overflow-hidden">
            <ZoneMapWithGeoAreas
              isPublic={true}
              tenantId={website.organization.id}
            />
          </div>
        </Suspense>
        {serviceAreaMap && (
          <Box className="mt-8" data-variable="SERVICE_AREA_MAP_IMAGE">
            <Image
              src={serviceAreaMap}
              alt="Service area map"
              className="w-full rounded-lg"
            />
          </Box>
        )}
      </Flex>
    </Container>
  );
};
